/**
 * Admin Dashboard Page - Wrapped in AppLayout
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  ManageAccounts as ManageAccountsIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';

import useAdminStore from '../store/adminStore';
import useAuthStore from '../store/authStore';
import UserManagement from '../components/UserManagement';
import ServiceMetrics from '../components/ServiceMetrics';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    dashboard,
    loading,
    error,
    loadDashboard,
    clearCache,
    clearingCache,
  } = useAdminStore();

  const [currentTab, setCurrentTab] = useState(0);
  const { t } = useTranslation(['common', 'admin']);

  useEffect(() => {
    if (currentTab === 0) {
      loadDashboard();
      const interval = setInterval(() => {
        loadDashboard();
      }, 30000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [loadDashboard, currentTab]);

  const handleClearCache = async () => {
    if (window.confirm(t('confirm.clearCache', { ns: 'admin' }))) {
      await clearCache();
      await loadDashboard();
    }
  };

  if (loading && !dashboard) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <strong>{t('alerts.errorPrefix', { ns: 'admin' })}</strong> {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: '#f5f7fa',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {t('title', { ns: 'admin' })}
            </Typography>
            <Chip
              icon={<PeopleIcon />}
              label={t('userChip', { ns: 'admin', role: user?.role, name: user?.full_name })}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>
          <Tooltip title={t('refresh', { ns: 'admin' })}>
            <span>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => currentTab === 0 && loadDashboard()}
                disabled={loading || currentTab !== 0}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                {t('refresh', { ns: 'admin' })}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Tabs */}
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 600,
            },
            '& .Mui-selected': {
              color: 'white !important',
            },
            '& .MuiTabs-indicator': {
              bgcolor: 'white',
              height: 3,
            },
          }}
        >
          <Tab icon={<DashboardIcon />} label={t('tabs.dashboard', { ns: 'admin' })} iconPosition="start" />
          {user?.role === 'MASTER' && (
            <Tab
              icon={<ManageAccountsIcon />}
              label={t('tabs.userManagement', { ns: 'admin' })}
              iconPosition="start"
            />
          )}
        </Tabs>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {currentTab === 0 && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
              {t('sections.serviceMonitoring', { ns: 'admin' })}
            </Typography>
            <ServiceMetrics />

            <Typography variant="h6" gutterBottom sx={{ mb: 2, mt: 4, fontWeight: 700 }}>
              {t('sections.systemMetrics', { ns: 'admin' })}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title={t('metrics.activeUsers', { ns: 'admin' })}
                  value={dashboard?.system_metrics?.active_users || 0}
                  icon={<PeopleIcon sx={{ fontSize: 40, color: '#667eea' }} />}
                  subtitle={t('metrics.last24h', { ns: 'admin' })}
                  color="#667eea"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title={t('metrics.totalRequests', { ns: 'admin' })}
                  value={dashboard?.system_metrics?.total_requests || 0}
                  icon={<SpeedIcon sx={{ fontSize: 40, color: '#2e7d32' }} />}
                  subtitle={t('metrics.last24h', { ns: 'admin' })}
                  color="#2e7d32"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title={t('metrics.avgResponse', { ns: 'admin' })}
                  value={`${dashboard?.system_metrics?.average_response_time_ms?.toFixed(0) || 0}ms`}
                  icon={<SpeedIcon sx={{ fontSize: 40, color: '#f57c00' }} />}
                  subtitle={t('metrics.responseTime', { ns: 'admin' })}
                  color="#f57c00"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MetricCard
                  title={t('metrics.totalErrors', { ns: 'admin' })}
                  value={dashboard?.system_metrics?.total_errors || 0}
                  icon={<ErrorIcon sx={{ fontSize: 40, color: '#d32f2f' }} />}
                  subtitle={t('metrics.last24h', { ns: 'admin' })}
                  color="#d32f2f"
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 700 }}>
              {t('sections.cacheStats', { ns: 'admin' })}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <CacheStatsCard
                  stats={dashboard?.cache_stats}
                  onClearCache={handleClearCache}
                  clearingCache={clearingCache}
                  t={t}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatabaseStatsCard stats={dashboard?.db_stats} t={t} />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/chat')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                ← {t('buttons.backToChat', { ns: 'admin' })}
              </Button>
            </Box>
          </>
        )}

        {currentTab === 1 && user?.role === 'MASTER' && <UserManagement />}
      </Container>
    </Box>
  );
}

function MetricCard({ title, value, icon, subtitle, color }) {
  return (
    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h3" sx={{ fontWeight: 700, color, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );
}

function CacheStatsCard({ stats, onClearCache, clearingCache, t }) {
  if (!stats) return null;

  const hitRate = stats.hit_rate || 0;

  return (
    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <MemoryIcon sx={{ fontSize: 32, color: '#f57c00' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('cache.title', { ns: 'admin' })}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('cache.hitRate', { ns: 'admin' })}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {hitRate.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={hitRate}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  hitRate > 50 ? '#4caf50' : hitRate > 30 ? '#ff9800' : '#f44336',
                borderRadius: 4,
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('cache.memoryUsed', { ns: 'admin' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {stats.used_memory_human || 'N/A'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('cache.hitsMisses', { ns: 'admin' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {stats.keyspace_hits} / {stats.keyspace_misses}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {t('cache.connectedClients', { ns: 'admin' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {stats.connected_clients || 0}
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          color="warning"
          onClick={onClearCache}
          disabled={clearingCache || !stats.enabled}
          sx={{ borderRadius: 2 }}
        >
          {clearingCache ? t('cache.clearing', { ns: 'admin' }) : t('cache.clear', { ns: 'admin' })}
        </Button>
      </CardContent>
    </Card>
  );
}

function DatabaseStatsCard({ stats, t }) {
  if (!stats) return null;

  const connectionUsage = (stats.active_connections / stats.max_connections) * 100;

  return (
    <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <StorageIcon sx={{ fontSize: 32, color: '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('database.title', { ns: 'admin' })}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('database.connectionPool', { ns: 'admin' })}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {stats.active_connections} / {stats.max_connections}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={connectionUsage}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  connectionUsage < 70
                    ? '#4caf50'
                    : connectionUsage < 90
                    ? '#ff9800'
                    : '#f44336',
                borderRadius: 4,
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('database.totalSessions', { ns: 'admin' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {stats.total_sessions.toLocaleString()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('database.totalMessages', { ns: 'admin' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {stats.total_messages.toLocaleString()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {t('database.activeUsers', { ns: 'admin' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {stats.total_users}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

